---
title: Wave shaper
description: "A wave shaper synth device: a waveform, a drive, a tone, a detune, a mix and a volume, a panel with a pad that plays it, a Web Audio engine with noteOn and noteOff, and tools for agents."
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="block-wave-shaper" />

Two detuned oscillators, a distortion mixed in with the clean sound, and a low-pass filter. Press the pad: the pitch goes across and the resonance goes up. It is a [device](/docs/devices): a state, a panel, an engine and tools, each its own file.

## Installation

<Install name="wave-shaper" kind="device" />

## Controls

<DeviceControls device="wave-shaper">
<Control id="wave">The wave of the oscillators.</Control>
<Control id="drive">How much of the distortion is pushed into the sound.</Control>
<Control id="tone">The cutoff of the low-pass filter.</Control>
<Control id="detune">How far apart the two oscillators are tuned.</Control>
<Control id="mix">The balance of the distorted signal and the clean one.</Control>
<Control id="volume">The level of the output.</Control>
</DeviceControls>

## Panel

The panel has a pad that plays the synth: the pitch goes across and the resonance goes up. `waveShaperPitch(position)` and `waveShaperResonance(position)` turn a position from 0 to 1 into a note.

<DeviceUsage name="wave-shaper" />

## Engine

Create it, connect it and update it as for any device: see [The engine](/docs/devices#the-engine).

```ts
engine.noteOn(note); // starts a note now, or glides the one being held
engine.noteOff(); // lets it go

// Scheduled ahead on the audio clock, by a host that plays notes from a song:
engine.noteOn({ ...note, velocity: 0.8 }, { at: time, id: "c4" });
engine.noteOff({ at: time + length, id: "c4" });
```

One voice at a time, as the [pocket synth](/docs/components/base/pocket-synth). The distortion curve is a table of 44,100 points, rebuilt only when the drive changes. The shaper oversamples 4x, which delays its path by 4 ms, and the clean path waits as long so the mix does not comb.
